import { forwardRef } from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "./lib/cn.js";

/**
 * Switch — chrome-vocabulary toggle.
 *
 * Sharp-cornered track + thumb (no rounded-full pill); hairline border;
 * checked = accent fill, unchecked = chrome strong-bg fill. Reads as a
 * power switch on a console, not a softer iOS pill.
 */
export const Switch = forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    data-pp-switch
    className={cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center",
      "border border-[var(--pp-border)] transition-colors",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:bg-[var(--accent)] data-[state=checked]:border-[var(--accent)]",
      "data-[state=unchecked]:bg-[var(--pp-bg-strong)]",
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        "pointer-events-none block h-4 w-4 bg-[var(--pp-text)]",
        "transition-transform",
        "data-[state=checked]:translate-x-4 data-[state=checked]:bg-[var(--pp-text-on-accent)]",
        "data-[state=unchecked]:translate-x-0",
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;
